import {
  CardReissuanceReason,
  InstructionActions,
  ReissuedCardReceivingType
} from "@/modules/application/common/enum";
import type {
  ICardReissuanceDetails,
  ICardServiceInstructionApplicationForm,
  ICardServiceInstructionApplicationRequest,
} from "@/modules/application/common/types";
import {
  getCardReissuanceReasonOptions,
  getCardSubTypeOptions,
  getChangeCardTypeOptions,
  getDoInshuranceOptions,
  getInstructionActionOptions,
  getReissuedCardReceivingTypeOptions
} from "@/modules/application/helpers";
import {
  useCreateCardServiceInstructionApplicationMutation,
  useGetCardServiceInstructionApplicationQuery,
  useLazyGetBankBranchesQuery,
  useUpdateCardServiceInstructionApplicationMutation
} from "@/modules/application/services";
import { useGetCardsQuery } from "@/modules/card/services";
import { INITIAL_PAGE, INPUT_MAX_LIMIT, } from "@/shared/common/constants";
import type { IExtendedObject, IOperationPageProps, IOption } from "@/shared/common/types";
import {
  CreditCardSelectOption,
  DropdownIndicator,
  InfoContainer,
  Select
} from "@/shared/components";
import { ApplicationLayout } from "@/shared/components/ApplicationLayout";
import {
  formatCardNumber,
  isValidPhoneNumber,
  removeAllSpacesAndDashes,
  trimAll,
} from "@/shared/helpers";
import { formatPhoneNumber } from "@/shared/helpers/commonHelpers";
import { useHasFormChanges } from "@/shared/hooks";
import { RadioGroup } from "@ab.uitools/ui-library";
import { Checkbox } from "@ab.uitools/ui-library/components/Checkbox";
import { CollapseGroup } from "@ab.uitools/ui-library/components/Collapse";
import { FormField } from "@ab.uitools/ui-library/components/FormField";
import { Input } from "@ab.uitools/ui-library/components/Input";
import { IconInfoFilled } from "@ab.uitools/ui-library/components/SVGIcons";
import { Textarea } from "@ab.uitools/ui-library/components/Textarea";
import { Tooltip } from "@ab.uitools/ui-library/components/Tooltip";
import { Positions } from "@ab.uitools/ui-library/components/Tooltip/types";
import { useFormProps } from "@ab.uitools/ui-library/hooks/useFormProps";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import uuid4 from "uuid4";

import { ApplicationTermsAndConditionsLink } from "../ApplicationTermsAndConditionsLink";

export const CardServiceInstructionApplicationForm = ({ isEdit, isCopy }: IOperationPageProps) => {
  const { t } = useTranslation();
  const { operationId = "" } = useParams();
  const idempotencyKey = useRef(uuid4()).current;
  const { errors = {}, watch, reset } = useFormProps();

  const [defaultData, setDefaultData] = useState<IExtendedObject>({});
  const [bankBranchOptions, setBankBranchOptions] = useState<IOption[]>([]);
  const [cardInputValue, setCardInputValue] = useState<string>("");

  const [getBankBranches] = useLazyGetBankBranchesQuery();
  const {
    data: cards = []
  } = useGetCardsQuery({
    pageIndex: INITIAL_PAGE,
    pageSize: 100,
  }, {
    refetchOnMountOrArgChange: true,
  });

  const {
    data: cardServiceInstructionApplication,
    isLoading: isGetApplicationLoading,
  } = useGetCardServiceInstructionApplicationQuery({
    id: operationId
  }, {
    refetchOnMountOrArgChange: true,
    skip: (!isEdit || !isCopy) && !operationId
  });

  const [
    createCardServiceInstructionApplicationApi,
    {
      isLoading: isCreateLoading
    }
  ] = useCreateCardServiceInstructionApplicationMutation();

  const [
    updateCardServiceInstructionApplicationApi,
    {
      isLoading: isUpdateLoading
    }
  ] = useUpdateCardServiceInstructionApplicationMutation();

  const buildReissuance = (details: ICardReissuanceDetails) => {
    const result: ICardReissuanceDetails = {
      cardReissuanceReason: details.cardReissuanceReason,
      reissuedCardReceivingType: details.reissuedCardReceivingType,
    };
    if (details.cardReissuanceReason === CardReissuanceReason.CardTypeChange) {
      result.cardChangeType = details.cardChangeType;
      result.cardType = details.cardType;
    }
    if (details.reissuedCardReceivingType === ReissuedCardReceivingType.Delivery) {
      result.reissuedCardReceivingAddress = details.reissuedCardReceivingAddress;
    }
    if (details.reissuedCardReceivingType === ReissuedCardReceivingType.BankBranch) {
      result.bankBranchCode = details.bankBranchCode;
    }
    return result;
  };

  const buildPaymentCardRequestBody = (values: ICardServiceInstructionApplicationForm) => {
    const base: ICardServiceInstructionApplicationRequest = {
      action: values.action,
      creditCardNumber: values.creditCardNumber,
      phoneNumber: trimAll(values.phoneNumber),
      applicationNumber: values.applicationNumber,
      purpose: values.purpose,
    };

    switch (values.action) {
      case InstructionActions.CardInsurance:
        return {
          ...base,
          doInshurance: values.doInshurance,
        };
      case InstructionActions.PasswordChange:
        return {
          ...base,
          passwordReceivingPhoneNumber: trimAll(values.passwordReceivingPhoneNumber || ""),
        };
      case InstructionActions.CardReissuance: {
        const details = values.cardReissuanceDetails;
        if (!details) return base;

        return {
          ...base,
          cardReissuanceDetails: buildReissuance(details),
        };
      }
      default:
        return base;
    }
  };

  const cardServiceInstructionApplicationAction = useCallback(
    (values: ICardServiceInstructionApplicationForm) => {
      const requestData = buildPaymentCardRequestBody(values);
      return isEdit ? updateCardServiceInstructionApplicationApi({
          data: requestData,
          id: `${cardServiceInstructionApplication?.id}`,
        }).unwrap() :
        createCardServiceInstructionApplicationApi({
          data: requestData,
          idempotencyKey,
        }).unwrap();
    }, [cardServiceInstructionApplication]);

  const cardsList = useMemo(() => {
    return cards.map((item, index) => ({
      value: item.number,
      label: item.number,
      meta: {
        name: item.holder,
        title: item.name || item.description,
        index,
      },
    }));
  }, [cards]);

  const values = watch?.() || {};

  const setDefaultValues = () => {
    const data = {
      action: cardServiceInstructionApplication?.action,
      creditCardNumber: cardServiceInstructionApplication?.creditCardNumber,
      phoneNumber: cardServiceInstructionApplication?.phoneNumber,
      passwordReceivingPhoneNumber: cardServiceInstructionApplication?.passwordReceivingPhoneNumber,
      doInshurance: cardServiceInstructionApplication?.doInshurance,
      applicationNumber: isEdit ?
        cardServiceInstructionApplication?.applicationNumber : values.applicationNumber,
      cardReissuanceDetails: cardServiceInstructionApplication?.cardReissuanceDetails,
      purpose: cardServiceInstructionApplication?.purpose,
    };
    reset?.(data);
    setDefaultData(data);
  };

  const currentData = useMemo(() => ({
    action: values.action,
    phoneNumber: values.phoneNumber,
    passwordReceivingPhoneNumber: values.passwordReceivingPhoneNumber,
    doInshurance: values.doInshurance,
    creditCardNumber: values.creditCardNumber,
    applicationNumber: values.applicationNumber,
    cardReissuanceDetails: values.cardReissuanceDetails,
    purpose: values.purpose,
  }), [values]);

  const hasChanges = useHasFormChanges(defaultData, currentData);

  useEffect(() => {
    if ((isEdit || isCopy) && !!cardServiceInstructionApplication?.id) {
      setDefaultValues();
    }
  }, [cardServiceInstructionApplication]);

  useEffect(() => {
    getBankBranches().unwrap().then((data) => {
      const options = data.map((branch) => ({
        label: branch.description,
        value: branch.code,
      }));
      setBankBranchOptions(options);
    });
  }, []);

  return (
    <ApplicationLayout
      title={t("application.paymentCardServiceInstruction.title")}
      page="Payment Card Service Instruction application"
      isEdit={isEdit}
      hasChanges={hasChanges}
      isLoading={isCreateLoading || isUpdateLoading}
      isGetApplicationLoading={isGetApplicationLoading}
      getApplicationId={cardServiceInstructionApplicationAction}
    >
      <CollapseGroup
        className="collapse-group"
        items={[
          {
            content: <>
              <FormField
                name="creditCardNumber"
                className="mb-32"
                errorMessageIcon="infoFilled"
                As={(props) => (
                  <Select
                    {...props}
                    required
                    isSearchable
                    className="custom-select"
                    options={cardsList}
                    inputValue={cardInputValue}
                    label={t("application.cardNumber")}
                    placeholder={t("application.typeOrChooseACard")}
                    formatOptionLabel={(option) => {
                      return formatCardNumber({ card: `${option?.value}` });
                    }}
                    onInputChange={(val, { action }) => {
                      if (action === "input-change") {
                        const raw = removeAllSpacesAndDashes(val);
                        const formatted = formatCardNumber({ card: raw });
                        setCardInputValue(formatted);
                      }
                    }}
                    components={{ Option: CreditCardSelectOption }}
                  />
                )}
              />
              <FormField
                name="action"
                className="mb-32"
                shouldValidate
                errorMessageIcon="infoFilled"
                As={(props) => (
                  <Select
                    {...props}
                    required
                    options={getInstructionActionOptions({ t })}
                    label={t("application.paymentCardServiceInstruction.action")}
                    placeholder={t("application.paymentCardServiceInstruction.chooseAction")}
                  />
                )}
              />
              {values.action === InstructionActions.CardInsurance && (
                <FormField
                  name="doInshurance"
                  className="mb-32 mt-16"
                  errorMessageIcon="infoFilled"
                  As={(props) => (
                    <RadioGroup
                      {...props}
                      selected={values.doInshurance}
                      options={getDoInshuranceOptions({ t })}
                    />
                  )}
                />
              )}
              {values.action === InstructionActions.PasswordChange && (
                <FormField
                  name="passwordReceivingPhoneNumber"
                  className="mb-32"
                  errorMessageIcon="infoFilled"
                  As={(props) => (
                    <Input
                      {...props}
                      required
                      label={t(
                        "application.paymentCardServiceInstruction.phoneNumberForReceivingPassword")}
                      placeholder={t("application.enterPhoneNumber")}
                    />
                  )}
                />
              )}
              <FormField
                name="phoneNumber"
                className="mb-32"
                errorMessageIcon="infoFilled"
                As={(props) => {
                  const { onChange, ...rest } = props;
                  return (
                    <Input
                      {...rest}
                      label={t("general.phoneNumber")}
                      placeholder={t("general.enterPhoneNumber")}
                      onChange={(e) => {
                        e.target.value = formatPhoneNumber(e.target.value);
                        onChange?.(e);
                      }}
                      isAllowed={({ formattedValue }) => isValidPhoneNumber(formattedValue)}
                      labelAddons={
                        <div>
                          <Tooltip
                            position={Positions.MIDDLE_RIGHT}
                            id="phone-number-info"
                            text={t("application.paymentCardServiceInstruction.phoneNumberInfo")}
                          />
                          <IconInfoFilled
                            className="ml-4 cursor-pointer"
                            size="small"
                            id="phone-number-info"
                            type="quaternary"
                          />
                        </div>
                      }
                    />
                  );
                }}
              />
              <FormField
                name="purpose"
                className="mb-12"
                errorMessageIcon="infoFilled"
                hideErrorMessage
                As={(props) => (
                  <Textarea
                    {...props}
                    required
                    error={`${errors?.purpose?.message || ""}`}
                    label={t("general.purpose")}
                    placeHolder={t("general.enterYourPurpose")}
                    maxCount={INPUT_MAX_LIMIT}
                  />
                )}
              />
              {values.action === InstructionActions.CardBlocking && (
                <InfoContainer
                  className="mb-24"
                  content={(
                    <>
                      {t("application.paymentCardServiceInstruction.urgentCardBlockingInfo")}
                      <strong>(+374) 10 56 11 11.</strong>
                    </>
                  )}
                />
              )}
              {values.action === InstructionActions.CardReissuance && (
                <>
                  <FormField
                    name="cardReissuanceDetails.cardReissuanceReason"
                    className="mb-32"
                    errorMessageIcon="infoFilled"
                    shouldValidate
                    As={(props) => (
                      <Select
                        {...props}
                        options={getCardReissuanceReasonOptions({ t })}
                        label={t("application.paymentCardServiceInstruction.reasonForReissuance")}
                        placeholder={t(
                          "application.paymentCardServiceInstruction.chooseReasonForReissuance")}
                      />
                    )}
                  />
                  {values.cardReissuanceDetails?.cardReissuanceReason === CardReissuanceReason.CardTypeChange && (
                    <>
                      <FormField
                        name="cardReissuanceDetails.cardChangeType"
                        className="mb-32"
                        errorMessageIcon="infoFilled"
                        shouldValidate
                        As={(props) => (
                          <Select
                            {...props}
                            options={getChangeCardTypeOptions({ t })}
                            label={t("application.paymentCardServiceInstruction.cardChangeType")}
                            placeholder={t(
                              "application.paymentCardServiceInstruction.chooseCardChangeType")}
                            components={{ DropdownIndicator }}
                          />
                        )}
                      />
                      <FormField
                        name="cardReissuanceDetails.cardType"
                        className="mb-32"
                        errorMessageIcon="infoFilled"
                        shouldValidate
                        As={(props) => (
                          <Select
                            {...props}
                            options={getCardSubTypeOptions({ t })}
                            label={t("application.paymentCardServiceInstruction.cardType")}
                            placeholder={t(
                              "application.paymentCardServiceInstruction.chooseCardType")}
                          />
                        )}
                      />
                    </>
                  )}
                  <FormField
                    name="cardReissuanceDetails.reissuedCardReceivingType"
                    className="mb-32"
                    errorMessageIcon="infoFilled"
                    shouldValidate
                    As={(props) => (
                      <Select
                        {...props}
                        options={getReissuedCardReceivingTypeOptions({ t })}
                        label={t(
                          "application.paymentCardServiceInstruction.reissuedCardReceivingType")}
                        placeholder={t(
                          "application.paymentCardServiceInstruction.chooseReissuedCardReceivingType")}
                      />
                    )}
                  />
                  {values.cardReissuanceDetails?.reissuedCardReceivingType ===
                    ReissuedCardReceivingType.Delivery && (
                      <FormField
                        name="cardReissuanceDetails.reissuedCardReceivingAddress"
                        className="mb-32"
                        errorMessageIcon="infoFilled"
                        As={(props) => (
                          <Input
                            {...props}
                            required
                            label={t(
                              "application.paymentCardServiceInstruction.reissuedCardReceivingAddress")}
                            placeholder={t("application.enterReissuedCardReceivingAddress")}
                          />
                        )}
                      />
                    )
                  }
                  {values.cardReissuanceDetails?.reissuedCardReceivingType ===
                    ReissuedCardReceivingType.BankBranch && (
                      <FormField
                        name="cardReissuanceDetails.bankBranchCode"
                        className="mb-32"
                        errorMessageIcon="infoFilled"
                        shouldValidate
                        As={(props) => (
                          <Select
                            {...props}
                            options={bankBranchOptions}
                            label={t(
                              "application.paymentCardServiceInstruction.bankBranch")}
                            placeholder={t(
                              "application.paymentCardServiceInstruction.chooseBankBranchCode")}
                          />
                        )}
                      />
                    )
                  }
                </>
              )}
              <FormField
                name="isAgreeWithConditions"
                className="mb-8"
                errorMessageIcon="infoFilled"
                As={(props) => (
                  <Checkbox
                    {...props}
                    isInvalid={!!errors.isAgreeWithConditions}
                    label={<ApplicationTermsAndConditionsLink/>}
                  />
                )}
              />
            </>,
            title: t("application.applicationDetails"),
            value: "application_details",
            isOpen: true,
          },
          {
            content: <>
              <FormField
                name="applicationNumber"
                className="mb-8"
                errorMessageIcon="infoFilled"
                As={(props) => (
                  <Input
                    {...props}
                    required
                    label={t("application.applicationNumber")}
                    placeholder={t("application.enterApplicationNumber")}
                  />
                )}
              />
            </>,
            title: t("general.otherDetails"),
            value: "other_details",
            isOpen: true,
          },
        ]}
      />
    </ApplicationLayout>
  );
};
